import { motion } from 'framer-motion';
import { MetricCard } from './MetricCard';

export function BizPlanHero() {
  return (
    <section
      id="hero"
      className="relative min-h-screen overflow-hidden pt-28 pb-20"
      style={{ background: 'linear-gradient(135deg, #0F1B2E 0%, #1A365D 60%, #0F3D2E 100%)' }}
    >
      {/* Grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      {/* Glow */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[600px] w-[800px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #10b981 0%, transparent 60%)' }}
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex justify-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm text-emerald-400 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Oportunidad de inversion — Abril 2026
          </span>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6 text-center"
        >
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-[3.5rem]">
            500.000 personas necesitan{' '}
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-400 bg-clip-text text-transparent">
              regularizarse en Espana
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Migro es la unica plataforma que combina IA legal, un CRM para abogados, y servicios financieros
            para migrantes — todo en un ecosistema que se retroalimenta.
          </p>
          <p className="mx-auto mt-3 max-w-xl text-base font-semibold text-orange-400">
            Modelo de suscripcion: cada cliente paga 48-68 EUR/mes durante 10 meses. Ingresos recurrentes desde el dia 1.
          </p>
        </motion.div>

        {/* Divider */}
        <div className="mx-auto mb-12 h-px w-24 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />

        {/* Key fact banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 rounded-2xl border border-orange-500/30 bg-orange-500/10 p-5 text-center backdrop-blur-md"
        >
          <p className="text-sm text-orange-300">
            Cada <span className="font-bold text-white">2 EUR</span> en publicidad generan un contacto.
            El <span className="font-bold text-white">6%</span> se suscribe.
            Suscripcion media: <span className="font-bold text-white">58 EUR/mes x 10 meses = 580 EUR de LTV</span>.{' '}
            <span className="font-bold text-orange-200">Coste por suscriptor: 33 EUR. Retorno: 17x.</span>
          </p>
        </motion.div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard
            label="Facturacion Marzo 2026"
            value={1183}
            prefix=""
            suffix=" EUR"
            color="emerald"
            delay={0}
            icon={
              <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <MetricCard
            label="MRR objetivo mes 6"
            value={10440}
            prefix=""
            suffix=" EUR"
            color="blue"
            delay={0.1}
            icon={
              <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.306a11.95 11.95 0 015.814-5.518l2.74-1.22m0 0l-5.94-2.281m5.94 2.28l-2.28 5.941" />
              </svg>
            }
          />
          <MetricCard
            label="Suscripcion media"
            value={58}
            prefix=""
            suffix=" EUR/mes"
            color="orange"
            delay={0.2}
            icon={
              <svg className="h-5 w-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
              </svg>
            }
          />
          <MetricCard
            label="LTV por suscriptor"
            value={580}
            prefix=""
            suffix=" EUR"
            color="purple"
            delay={0.3}
            icon={
              <svg className="h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              </svg>
            }
          />
        </div>
      </div>
    </section>
  );
}
